// src/components/dashboard/HealthDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Upload, Activity, CheckCircle } from 'lucide-react';
import { apiService } from '@/services/api';
import { AggregateScoreOrbit } from './AggregateScoreOrbit';
import { AIAnalysisSection } from './AIAnalysisSection';
import { UploadModal } from './UploadModal';
import { ProcessingInfo } from '@/components/health/ProcessingInfo';
import { useRealtimeHealthProfile } from '@/hooks/useRealtimeHealthProfile';
import { isRealtimeEnabled } from '@/lib/supabase';
import { loadSampleDocument } from '@/utils/sampleDocument';


interface HealthDashboardProps {
  onShowPreview?: (data: any, jobId: string) => void;
  onNavigateToCategories?: () => void;
  onNavigateToPrescription?: () => void;
  isUploadModalOpen?: boolean;
  isDemo?: boolean;
}

export const HealthDashboard: React.FC<HealthDashboardProps> = ({
  onShowPreview,
  onNavigateToCategories,
  onNavigateToPrescription,
  isUploadModalOpen: externalIsUploadModalOpen,
  isDemo = false
}) => {
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [isLoadingSample, setIsLoadingSample] = useState(false);
  const [processingInfo, setProcessingInfo] = useState<{
    fileName: string;
    progress: number;
    stage: string;
    status: 'processing' | 'completed' | 'failed';
    errorCode?: string;
    errorType?: string;
    errorMessage?: string;
  } | null>(null);
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  // Get user ID from token on mount
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      try {
        // Decode JWT to get user_id (JWT format: header.payload.signature)
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.sub || payload.user_id);
      } catch (error) {
        console.error('[HealthDashboard] Failed to decode token:', error);
      }
    }
  }, []);

  // Sync external upload modal state
  useEffect(() => {
    if (externalIsUploadModalOpen !== undefined) {
      setIsUploadModalOpen(externalIsUploadModalOpen);
    }
  }, [externalIsUploadModalOpen]);

  // Fetch health summary and score with React Query
  const { data: summaryData, isLoading: summaryLoading, error: summaryError, refetch: refetchSummary } = useQuery({
    queryKey: ['healthSummary'],
    queryFn: async () => {
      console.log('🔄 [HealthDashboard] Fetching health summary...');
      const response = await apiService.getUserHealthSummary();
      console.log('📥 [HealthDashboard] Health summary response:', {
        success: response.success,
        hasData: !!response.data,
        score: response.data?.overall_health_score,
        timestamp: new Date().toISOString()
      });
      // Return data even if null/empty - this is a valid state for new users
      if (response.success) {
        return response.data || null;
      }
      throw new Error('Failed to fetch health summary');
    },
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    refetchOnMount: !isDemo, // Only refetch if not in demo mode
    refetchOnWindowFocus: false,
    retry: false,
    enabled: !isDemo, // Disable query in demo mode
  });

  const error = summaryError;

  // Real-time health profile (score + summary)
  const {
    healthScore: realtimeHealthScore,
    healthSummary: realtimeHealthSummary,
    isConnected: profileConnected,
    setHealthScore: setRealtimeHealthScore,
    setHealthSummary: setRealtimeHealthSummary,
  } = useRealtimeHealthProfile({
    userId,
    initialScore: summaryData?.overall_health_score || null,
    initialSummary: summaryData?.health_summary || null,
  });

  // Use real-time data if available, otherwise fall back to API data
  const healthScore = realtimeHealthScore ?? summaryData?.overall_health_score ?? null;
  const healthSummary = realtimeHealthSummary ?? summaryData?.health_summary ?? null;
  const clinicalAnalysis = summaryData?.ai_clinical_analysis ?? null;

  // Translation data from health summary
  const translationsReady = summaryData?.translations_ready ?? false;
  const currentLanguage = summaryData?.current_language ?? 'en';
  const preferredLanguage = summaryData?.preferred_language ?? 'en';

  // Debug translation state
  console.log('🌐 [HealthDashboard] Translation state:', {
    translationsReady,
    currentLanguage,
    preferredLanguage,
    isChangingLanguage,
  });

  console.log('📊 [HealthDashboard] Current state:', {
    healthScore,
    summaryLoading,
    realtimeEnabled: isRealtimeEnabled(),
    profileConnected,
  });

  // Update real-time state when API data changes
  useEffect(() => {
    if (summaryData) {
      setRealtimeHealthScore(summaryData.overall_health_score || null);
      setRealtimeHealthSummary(summaryData.health_summary || null);
    }
  }, [summaryData, setRealtimeHealthScore, setRealtimeHealthSummary]);

  // Show real-time connection status
  useEffect(() => {
    if (isRealtimeEnabled()) {
      if (profileConnected) {
        console.log('✅ [HealthDashboard] Real-time updates active');
      } else if (!userId) {
        console.log('⏳ [HealthDashboard] Waiting for user authentication');
      }
    } else {
      console.log('ℹ️ [HealthDashboard] Real-time disabled - using manual refresh mode');
    }
  }, [profileConnected, userId]);

  // Poll for translation status when translations are not ready
  useEffect(() => {
    // Only poll if we have data but translations aren't ready yet
    if (!summaryData || translationsReady || isDemo) {
      console.log('[HealthDashboard] 🌐 Translation polling skipped:', {
        hasSummaryData: !!summaryData,
        translationsReady,
        isDemo
      });
      return;
    }

    console.log('[HealthDashboard] 🌐 Starting translation status polling...');

    const pollInterval = setInterval(() => {
      console.log('[HealthDashboard] 🌐 Polling for translation status...');
      refetchSummary();
    }, 3000); // Poll every 3 seconds

    // Cleanup on unmount or when translations become ready
    return () => {
      console.log('[HealthDashboard] 🌐 Stopping translation status polling');
      clearInterval(pollInterval);
    };
  }, [summaryData, translationsReady, isDemo, refetchSummary]);

  const loadDashboardData = () => {
    console.log('🔄 [HealthDashboard] Manual refetch triggered');
    refetchSummary();
  };

  // Handle language change
  const handleLanguageChange = async (language: 'en' | 'hi' | 'ml') => {
    console.log('[HealthDashboard] 🌐 Language change requested:', language);
    console.log('[HealthDashboard] 🌐 Current language:', currentLanguage);

    if (language === currentLanguage) {
      console.log('[HealthDashboard] 🌐 Same language selected, skipping');
      return;
    }

    setIsChangingLanguage(true);
    try {
      // Update language preference in backend
      console.log('[HealthDashboard] 🌐 Calling API to update language preference...');
      const response = await apiService.updateLanguagePreference(language);
      console.log('[HealthDashboard] 🌐 Language preference update response:', response);

      // Refetch health summary to get content in new language
      console.log('[HealthDashboard] 🌐 Refetching health summary with new language...');
      await refetchSummary();
      console.log('[HealthDashboard] 🌐 Health summary refetched successfully');
    } catch (error) {
      console.error('[HealthDashboard] ❌ Language change failed:', error);
    } finally {
      setIsChangingLanguage(false);
      console.log('[HealthDashboard] 🌐 Language change complete');
    }
  };

  // Handle loading sample document
  const handleLoadSampleDocument = async () => {
    setIsLoadingSample(true);
    try {
      // Load sample document (downloads to device and returns File object)
      const sampleFile = await loadSampleDocument();
      // Show processing screen
      setProcessingInfo({
        fileName: sampleFile.name,
        progress: 0,
        stage: 'Uploading document...',
        status: 'processing'
      });

      // Process the sample file through the API
      console.log('📤 [HealthDashboard] Starting sample document processing...');
      const asyncJobResponse = await apiService.extractHealthMetricsAsync(sampleFile);
      const jobId = asyncJobResponse.data.job_id;

      // Poll for completion with real-time progress updates

      const jobStatusResponse = await apiService.pollJobUntilComplete(
        jobId,
        (progress, stage) => {
          // Update processing info with real-time progress
          setProcessingInfo({
            fileName: sampleFile.name,
            progress,
            stage,
            status: 'processing'
          });
        }
      );

      const jobResultData = jobStatusResponse.data.result || jobStatusResponse.data.result_data || null;

      if (!jobResultData) {
        throw new Error('Processing completed but no result data received');
      }

      // Show completion
      setProcessingInfo({
        fileName: sampleFile.name,
        progress: 100,
        stage: 'Analysis complete!',
        status: 'completed'
      });

      // Wait a moment to show completion state
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Hide processing screen
      setProcessingInfo(null);


      // Show preview if callback exists
      if (onShowPreview) {
        onShowPreview(jobResultData, jobId);
      } else {
        // Refresh dashboard data
        loadDashboardData();
      }
    } catch (error: any) {
      console.error('❌ [HealthDashboard] Failed to load sample document:', error);

      const errorMessage = error.message || 'Failed to load sample document';
      const errorCode = error.code || '';
      const errorType = error.type || '';

      // Show error in processing screen
      setProcessingInfo({
        fileName: 'Full_body_tests.pdf',
        progress: 0,
        stage: errorMessage,
        status: 'failed',
        errorCode,
        errorType,
        errorMessage
      });


    } finally {
      setIsLoadingSample(false);
    }
  };


  // Show error state only if there's an actual error
  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to load dashboard data';
    const isEmailVerificationError = errorMessage.includes('EMAIL_VERIFICATION_REQUIRED') ||
      errorMessage.toLowerCase().includes('email verification');

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)',
        gap: 'var(--space-4)',
        padding: 'var(--space-6)'
      }}>
        {isEmailVerificationError ? (
          <>
            <div style={{
              fontSize: '48px',
              marginBottom: 'var(--space-2)'
            }}>
              🔐
            </div>
            <h2 style={{
              color: 'var(--color-error)',
              margin: 0,
              textAlign: 'center'
            }}>
              Session Refresh Required
            </h2>
            <p style={{
              color: 'var(--color-text-secondary)',
              textAlign: 'center',
              maxWidth: '500px',
              margin: 0
            }}>
              Your authentication session needs to be refreshed. This usually happens after email verification.
              Please log out and log back in to continue.
            </p>
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              marginTop: 'var(--space-2)'
            }}>
              <button
                onClick={() => {
                  localStorage.clear();
                  sessionStorage.clear();
                  window.location.reload();
                }}
                style={{
                  padding: 'var(--space-3) var(--space-6)',
                  backgroundColor: 'var(--color-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 'var(--radius)',
                  cursor: 'pointer',
                  fontSize: 'var(--text-base)',
                  fontWeight: 'var(--font-semibold)'
                }}
              >
                Log Out & Refresh
              </button>
            </div>
          </>
        ) : (
          <>
            <p style={{ color: 'var(--color-error)' }}>Error: {errorMessage}</p>
            <button
              onClick={loadDashboardData}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                backgroundColor: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius)',
                cursor: 'pointer'
              }}
            >
              Retry
            </button>
          </>
        )}
      </div>
    );
  }

  // Show loading state only while actively fetching data
  if (summaryLoading) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        fontFamily: 'var(--font-primary)',
        gap: 'var(--space-4)'
      }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid var(--color-border)',
          borderTopColor: 'var(--color-primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <p style={{ color: 'var(--color-muted)' }}>Loading health data...</p>
        <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  // Show empty state for first-time users
  const showEmptyState = healthScore === null && !summaryLoading && !error;

  return (
    <div style={{
      fontFamily: 'var(--font-primary)',
      maxWidth: '1400px',
      margin: '0 auto',
      padding: 'var(--space-6)'
    }}>
      {/* BETA WARNING BANNER */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #3B82F6',
        borderRadius: 'var(--radius)',
        padding: '16px 20px',
        marginBottom: 'var(--space-6)',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px'
      }}>
        <span style={{ fontSize: '20px', flexShrink: 0 }}>ℹ️</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: '#1E40AF', marginBottom: '4px' }}>
            BETA VERSION
          </div>
          <div style={{ fontSize: '13px', color: '#1E3A8A', lineHeight: '1.5' }}>
            Please verify all extracted metrics against your original reports. If you used a sample report, please compare it against the downloaded document. If you notice any discrepancies, please report them to help us improve.

          </div>
        </div>
      </div>

      {/* Empty State - First Time User Welcome */}
      {showEmptyState && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '3rem'
        }}>
          {/* 3-Step Guide Section */}
          <div style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            <h1 style={{
              fontSize: '2.5rem',
              fontWeight: 800,
              color: 'var(--color-primary)',
              marginBottom: '1rem'
            }}>
              Welcome to heal
            </h1>
            <p style={{
              fontSize: '1.125rem',
              color: 'var(--color-secondary)',
              marginBottom: '2.5rem'
            }}>
              Your health journey starts here. Follow these simple steps to begin.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: '2rem',
              marginBottom: '3rem'
            }}>
              {[
                {
                  step: '01',
                  title: 'Upload Report',
                  desc: 'Drop a lab report or prescription.',
                  icon: <Upload size={24} color="#3b82f6" />
                },
                {
                  step: '02',
                  title: 'Auto-Extract',
                  desc: 'AI identifies metrics automatically.',
                  icon: <Activity size={24} color="#3b82f6" />
                },
                {
                  step: '03',
                  title: 'Get Insights',
                  desc: 'See trends and clinical summaries.',
                  icon: <CheckCircle size={24} color="#3b82f6" />
                }
              ].map((item, i) => (
                <div key={i} style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '1rem'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: '50%',
                    background: 'rgba(59, 130, 246, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: '0 0 0.25rem 0' }}>{item.title}</h3>
                    <p style={{ fontSize: '0.875rem', color: 'var(--color-secondary)', margin: 0 }}>{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
              <button
                onClick={() => setIsUploadModalOpen(true)}
                style={{
                  padding: '0.75rem 2rem',
                  background: '#3b82f6',
                  color: 'white',
                  borderRadius: '12px',
                  fontWeight: 600,
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                Upload Your First Report
              </button>
              <button
                onClick={handleLoadSampleDocument}
                disabled={isLoadingSample}
                style={{
                  padding: '0.75rem 2rem',
                  background: 'transparent',
                  color: 'var(--color-primary)',
                  borderRadius: '12px',
                  fontWeight: 600,
                  border: '1px solid var(--color-border)',
                  cursor: 'pointer'
                }}
              >
                Try Sample Report
              </button>
            </div>
          </div>

          {/* Ghost Units - Dashboard Preview */}
          <div style={{
            opacity: 0.3,
            pointerEvents: 'none',
            userSelect: 'none',
            position: 'relative'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(to bottom, transparent, var(--color-background))',
              zIndex: 1
            }} />
            <div className="dashboard-grid">
              <AggregateScoreOrbit score={null} isSkeleton={true} />
              <AIAnalysisSection summary={null} isSkeleton={true} />
            </div>
          </div>
        </div>
      )}

      {/* Regular Dashboard Content - Only show when NOT in empty state */}
      {!showEmptyState && (
        <>
          {/* Header Section */}
          <div className="dashboard-grid">
            {/* Aggregate Score Orbit */}
            <AggregateScoreOrbit score={healthScore} />

            {/* AI Analysis Section */}
            <AIAnalysisSection
              summary={healthSummary}
              clinicalAnalysis={clinicalAnalysis}
              translationsReady={translationsReady}
              currentLanguage={currentLanguage}
              preferredLanguage={preferredLanguage}
              onLanguageChange={handleLanguageChange}
              isChangingLanguage={isChangingLanguage}
            />
          </div>

          {/* Upload Button */}
          <div style={{
            marginTop: 'var(--space-8)'
          }}>
            <button
              onClick={() => setIsUploadModalOpen(true)}
              className="upload-button"
              style={{
                width: '100%',
                padding: 'var(--space-4)',
                backgroundColor: 'var(--color-primary)',
                color: 'var(--color-background)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius)',
                cursor: 'pointer',
                fontFamily: 'var(--font-primary)',
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-semibold)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'var(--space-2)',
                transition: 'var(--transition-fast)',
                letterSpacing: '0.5px'
              }}
            >
              <Upload size={20} />
              <span>UPLOAD LAB REPORT</span>
            </button>

            {/* Link to Categories */}
            {onNavigateToCategories && (
              <div style={{
                marginTop: 'var(--space-3)',
                textAlign: 'center'
              }}>
                <button
                  onClick={onNavigateToCategories}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-primary)',
                    fontSize: 'var(--text-sm)',
                    fontFamily: 'var(--font-primary)',
                    cursor: 'pointer',
                    textDecoration: 'none',
                    transition: 'var(--transition-fast)',
                    padding: 'var(--space-2)',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--space-1)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.textDecoration = 'underline';
                    e.currentTarget.style.opacity = '0.8';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.textDecoration = 'none';
                    e.currentTarget.style.opacity = '1';
                  }}
                >
                  <span>View your extracted results</span>
                  <span style={{ fontSize: 'var(--text-xs)' }}>→</span>
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Responsive Grid Styles */}
      <style>{`
        .dashboard-grid {
          display: grid;
          gap: var(--space-6);
          margin-bottom: var(--space-12);
          align-items: start;
        }

        @media (min-width: 1024px) {
          .dashboard-grid {
            grid-template-columns: 1fr 1.8fr;
          }
        }

        @media (max-width: 1023px) {
          .dashboard-grid {
            grid-template-columns: 1fr;
          }
        }

        .upload-button:hover {
          opacity: 0.85;
          transform: translateY(-1px);
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      {/* Upload Modal */}
      <UploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploadComplete={() => {
          // Reload dashboard data after successful upload
          loadDashboardData();
        }}
        onShowPreview={onShowPreview}
        onNavigateToPrescription={onNavigateToPrescription}
      />

      {/* Processing Screen - Shown when loading sample document */}
      {processingInfo && (
        <ProcessingInfo
          fileName={processingInfo.fileName}
          progress={processingInfo.progress}
          stage={processingInfo.stage}
          status={processingInfo.status}
          variant="fullscreen"
          errorCode={processingInfo.errorCode}
          errorType={processingInfo.errorType}
          errorMessage={processingInfo.errorMessage}
          onRetry={() => {
            setProcessingInfo(null);
            setIsLoadingSample(false);
          }}
        />
      )}
    </div>
  );
};
